import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Typography } from '@mui/material';
import SkeletonCard from './SkeletonCard';
import StatsLoadingOverlay from './StatsLoadingOverlay';

const LoadingWrapper = ({
  isLoading,
  children,
  loadingType = 'skeleton', // 'skeleton', 'overlay', 'both'
  skeletonVariant = 'stat',
  skeletonCount = 4,
  overlayVariant = 'default',
  title = 'Loading...',
  subtitle = 'Please wait',
  gridProps = { xs: 12, sm: 6, md: 3 },
  containerSx = {},
  showSectionTitle = false,
  sectionTitle = 'Dashboard Stats',
}) => {
  if (!isLoading) {
    return children;
  }

  const renderSkeletonGrid = () => (
    <Grid container spacing={2}>
      {showSectionTitle && (
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 1, px: 2, opacity: 0.7 }}>
            {sectionTitle}
          </Typography>
        </Grid>
      )}
      {[...Array(skeletonCount)].map((_, index) => (
        <Grid item {...gridProps} key={index}>
          <SkeletonCard variant={skeletonVariant} />
        </Grid>
      ))}
    </Grid>
  );

  const renderOverlay = () => (
    <StatsLoadingOverlay isLoading={isLoading} title={title} subtitle={subtitle} variant={overlayVariant} />
  );

  const renderContent = () => {
    switch (loadingType) {
      case 'overlay':
        return (
          <Box sx={{ position: 'relative', ...containerSx }}>
            {children}
            {renderOverlay()}
          </Box>
        );

      case 'both':
        return (
          <Box sx={{ position: 'relative', ...containerSx }}>
            {renderSkeletonGrid()}
            {renderOverlay()}
          </Box>
        );

      default: // 'skeleton'
        return <Box sx={containerSx}>{renderSkeletonGrid()}</Box>;
    }
  };

  return renderContent();
};

LoadingWrapper.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  loadingType: PropTypes.oneOf(['skeleton', 'overlay', 'both']),
  skeletonVariant: PropTypes.oneOf(['stat', 'action', 'wide', 'indicator-summary']),
  skeletonCount: PropTypes.number,
  overlayVariant: PropTypes.oneOf(['default', 'minimal', 'advanced']),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  gridProps: PropTypes.object,
  containerSx: PropTypes.object,
  showSectionTitle: PropTypes.bool,
  sectionTitle: PropTypes.string,
};

LoadingWrapper.defaultProps = {
  loadingType: 'skeleton',
  skeletonVariant: 'stat',
  skeletonCount: 4,
  overlayVariant: 'default',
  title: 'Loading...',
  subtitle: 'Please wait',
  gridProps: { xs: 12, sm: 6, md: 3 },
  containerSx: {},
  showSectionTitle: false,
  sectionTitle: 'Dashboard Stats',
};

export default LoadingWrapper;
